import { createI18n } from 'vue-i18n';
import { logError } from '@mop/shared/utils/logger';
import { isClient } from '@mop/shared/utils/util';
import { localeList, cmsLivePreviewLanguageMappings, getLocalesFromCountry } from '@/i18n/localeList';
import type { LocaleObject, AvailableCountries, MopI18n } from '@/types/locale';

export default defineNuxtPlugin(async (nuxtApp) => {
  const { $storyblokLivePreview, $cookie } = nuxtApp.vueApp.$nuxt;
  const isStoryblokLivePreviewEnabled = Boolean($storyblokLivePreview.isEnabled);
  const router = useRouter();
  const headers = useRequestHeaders();

  const filteredLocaleList: LocaleObject[] = localeList.filter((locale) => {
    return !locale.isDisabled || isStoryblokLivePreviewEnabled;
  });

  let localeFromRoute = router.currentRoute.value.path.split('/')[1];
  if (isStoryblokLivePreviewEnabled && cmsLivePreviewLanguageMappings[localeFromRoute]) {
    localeFromRoute = cmsLivePreviewLanguageMappings[localeFromRoute];
  }

  const availableCountries: AvailableCountries = extractCountriesFromLocaleList(localeList);
  const locale: LocaleObject = localeList.find((config) => config.code === localeFromRoute) || localeList[0];

  const defaultLocale = filteredLocaleList[0]?.code || localeList[0].code;

  if (locale && $cookie.get(COOKIE.LANGUAGE) !== locale?.code) {
    $cookie.store(COOKIE.LANGUAGE, locale.code);
  }

  // @ts-ignore
  const country: string = getCountryFromLocale(locale);

  function extractCountriesFromLocaleList(localeList: LocaleObject[]): string[] {
    const countryList: string[] = [];
    localeList.forEach((locale: LocaleObject) => {
      const country: string = getCountryFromLocale(locale);
      if (countryList.includes(country)) {
        return;
      }
      countryList.push(country);
    });
    return countryList;
  }

  function getCountryFromLocale(locale: LocaleObject): string {
    return locale.code;
  }

  function formatDate(datetime: string, options: any) {
    const reducedTime: string = datetime.split('T')[0]?.replace(' ', 'T');
    const withoutYear: boolean = options?.withoutYear;
    const date: Date = new Date(Date.parse(reducedTime));
    const formattedDate = `${date.toLocaleDateString(locale!.code, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })}`;

    return withoutYear ? `${formattedDate.split('.')[0]}.${formattedDate.split('.')[1]}` : formattedDate;
  }

  let messagesForLocale = {};
  try {
    // Attempt to dynamically import the locale file
    messagesForLocale = await import(`@/i18n/locale/${locale.lang}.json`);
  } catch (error) {
    logError(`Failed to import locale file for ${locale.lang}`);
    logError(error);

    if (isClient) {
      nuxtApp.callHook('vue:error', error, null, '');
    }
  }

  const i18n = createI18n({
    legacy: false,
    globalInjection: true,
    locale: locale.lang,
    messages: {
      [locale.lang]: messagesForLocale,
    },
  });

  function localePath(route: string): string {
    const localizedRoute = router.resolve(`/${locale?.lang}/${route}`);
    return localizedRoute ? localizedRoute.fullPath : '';
  }

  // @ts-ignore
  const { t, te, tm } = i18n.global;
  nuxtApp.provide('mopI18n', {
    locale: locale.code,
    cmsFallbackLanguage: locale.cmsFallbackLanguage,
    lang: locale.lang,
    localePath,
    t,
    te,
    tm,
    country,
    alpha3: locale?.alpha3,
    availableCountries,
    formatDate,
    getLocalesFromCountry: (country) => getLocalesFromCountry(filteredLocaleList, country),
    getBestMatchingBrowserLocale: (country) =>
      getBestMatchingBrowserLocale(headers, filteredLocaleList, defaultLocale, country),
  } as MopI18n);
});
